import _ from 'lodash';

import { SignatureDynamicValueTypes } from 'constants/dynamicValues';
import { PrismWidgetType, ReactRenderingContent } from 'constants/formBuilder';
import { FormSchemaType } from 'constants/forms';
import { fontFamilyMap } from 'constants/options';
import { getImageDimensions } from 'utils/file';
import { getDynamicFieldDisplayNameWithPath } from 'utils/forms';

import {
  ANCHOR_VERTICAL_OFFSET_PX,
  IMAGE_DEFAULT_STYLE,
  PRISM_FONT_FAMILY_OPTIONS,
} from './PrismFormEditor.pickers.constants';

const isTypeExcludedFromPathSuffix = type =>
  SignatureDynamicValueTypes.includes(type) ||
  type === FormSchemaType.BLANK_SIGNATURE_LINE ||
  type === FormSchemaType.IMAGE;

// The legacy print and the Prism PDF show a checkbox's title beside the box when it has no
// label of its own, so the chip must show that title rather than a catalog name.
const isTitlePrintedAsLabel = attributes =>
  !attributes.dataDynamicLabel &&
  attributes.dataDynamicWithLabel !== false &&
  attributes.dataDynamicWithLabel !== 'false';

const isBoolTitleExcludedFromPathSuffix = ({ type, currentTitle, attributes }) =>
  type === FormSchemaType.BOOL && (!currentTitle || isTitlePrintedAsLabel(attributes));

const getDynamicFieldTitleWithPathSuffix = ({
  baseTitle,
  value,
  type,
  isTag,
  dynamicFieldsSchema,
  duplicateDynamicFieldNames,
}) => {
  if (isTag || isTypeExcludedFromPathSuffix(type) || !duplicateDynamicFieldNames) {
    return baseTitle;
  }

  return getDynamicFieldDisplayNameWithPath({
    dynamicFieldsSchema,
    duplicateDynamicFieldNames,
    value,
    displayName: baseTitle,
  });
};

export const anchorPositionFromRect = rect => ({
  left: Math.round(rect.x + rect.width / 2),
  top: Math.round(rect.y + rect.height + ANCHOR_VERTICAL_OFFSET_PX),
});

const isLogoImagePick = pick => pick?.type === FormSchemaType.IMAGE && pick.title === 'Logo';

// Like CKEditor, the logo prints at the default POS logo's measured size.
export const withMeasuredLogoStyle = async (pick, logoUrl) => {
  if (!isLogoImagePick(pick)) {
    return pick;
  }

  const style = { ...IMAGE_DEFAULT_STYLE, ...pick.style };
  const dimensions = await getImageDimensions(logoUrl, { scale: 1, size: style.size });

  return { ...pick, style: { ...style, ...dimensions } };
};

// Templates saved in CKEditor store font keys (`TimesNewRoman`, `AxiformaBold`) while the Prism list
// uses display names. Show the stored key under its display name and keep it as the value, so an
// untouched save writes the key back unchanged.
export const getFontFamilyOptions = storedFontFamily => {
  if (
    !storedFontFamily ||
    PRISM_FONT_FAMILY_OPTIONS.some(({ value }) => value === storedFontFamily)
  ) {
    return PRISM_FONT_FAMILY_OPTIONS;
  }

  const storedName = fontFamilyMap[storedFontFamily]?.name || storedFontFamily;
  const prismOption = PRISM_FONT_FAMILY_OPTIONS.find(({ name }) => name === storedName);

  if (prismOption) {
    return PRISM_FONT_FAMILY_OPTIONS.map(option =>
      option === prismOption ? { name: option.name, value: storedFontFamily } : option,
    );
  }

  return [...PRISM_FONT_FAMILY_OPTIONS, { name: storedName, value: storedFontFamily }];
};

export const toPrismAttributes = ({
  pick,
  savedIsTag,
  sourceAttributes,
  dynamicFieldsSchema,
  duplicateDynamicFieldNames,
}) => {
  const {
    title,
    value,
    type,
    label,
    style,
    action,
    withLabel,
    poaVerbiage,
    checkboxRenderType,
    checkboxInverseValue,
    dataDynamicPostfix,
    dataDynamicPrefix,
    dataDynamicMultiplier,
    addDays,
    ignoreUserDefinedStyles,
    isTag,
  } = pick;

  const isSignatureType = SignatureDynamicValueTypes.includes(type);
  const isCheckboxType = type === FormSchemaType.BOOL;
  const baseTitle = title || sourceAttributes?.dataDynamicTitle || '';
  const titleWithPath = getDynamicFieldTitleWithPathSuffix({
    baseTitle,
    value,
    type,
    isTag: isTag || savedIsTag,
    dynamicFieldsSchema,
    duplicateDynamicFieldNames,
  });

  const out = {
    widgetType: PrismWidgetType.DYNAMIC_FIELD,
    dataTemplateFieldType: ReactRenderingContent.DYNAMIC_FIELD,
    dataDynamicTitle: titleWithPath,
    dataDynamicValue: value || '',
    dataDynamicSlug: value || sourceAttributes?.dataDynamicSlug || '',
    dataDynamicType: type || '',
    dataDynamicLabel: (isCheckboxType ? label : label || title) || '',
  };

  // Like CKEditor: a checkbox carries no title, so an empty label prints a bare box.
  if (isCheckboxType) {
    out.dataDynamicTitle = '';
  }

  if (isTag || savedIsTag) {
    out.dataDynamicTitle = title;
  }

  if (isSignatureType) {
    out.dataDynamicTitle = label || title || '';
  }

  if (action !== undefined) {
    out.dataDynamicAction = action;
  }

  if (style !== undefined) {
    out.dataDynamicStyle = style;
  }

  if (checkboxRenderType !== undefined) {
    out.dataDynamicCheckboxRenderType = checkboxRenderType;
  }

  if (checkboxInverseValue !== undefined) {
    out.dataDynamicCheckboxInverseValue = checkboxInverseValue;
  }

  if (withLabel !== undefined) {
    out.dataDynamicWithLabel = withLabel;
  }

  if (poaVerbiage !== undefined) {
    out.dataDynamicUsePoaVerbiage = poaVerbiage;
  }

  if (dataDynamicPostfix !== undefined) {
    out.dataDynamicPostfix = dataDynamicPostfix;
  }

  if (dataDynamicPrefix !== undefined) {
    out.dataDynamicPrefix = dataDynamicPrefix;
  }

  if (dataDynamicMultiplier !== undefined) {
    out.dataDynamicMultiplier = dataDynamicMultiplier;
  }

  if (addDays !== undefined) {
    out.dataDynamicDateAddDays = addDays;
  }

  if (ignoreUserDefinedStyles !== undefined) {
    out.dataDynamicIgnoreUserDefinedStyles =
      ignoreUserDefinedStyles === 'true' || ignoreUserDefinedStyles === true;
  }

  return out;
};

// The chip label of a field from the field catalog, with the path suffix for duplicate names. It is
// display-only: Prism draws it on the canvas and never writes it into the template.
export const createDynamicFieldLabelResolver =
  ({ dynamicFieldsSchema, dynamicFieldsMap, duplicateDynamicFieldNames }) =>
  attributes => {
    const value = _.isNil(attributes.dataDynamicValue) ? '' : String(attributes.dataDynamicValue);
    const type = attributes.dataDynamicType || '';
    const currentTitle = attributes.dataDynamicTitle || '';

    if (
      !value ||
      isTypeExcludedFromPathSuffix(type) ||
      isBoolTitleExcludedFromPathSuffix({ type, currentTitle, attributes })
    ) {
      return null;
    }

    const field =
      dynamicFieldsMap?.[value] ||
      dynamicFieldsMap?.[value.toLowerCase()] ||
      dynamicFieldsMap?.[value.toUpperCase()];
    const displayName = field?.label || field?.title;

    if (!displayName) {
      return null;
    }

    return getDynamicFieldDisplayNameWithPath({
      dynamicFieldsSchema,
      duplicateDynamicFieldNames,
      value,
      displayName,
    });
  };
